// 크리에이터 등급제 라이브 데이터 공용 파생 로직.
//
// creator_tier_monthly / creator_tier_daily (Supabase project mehvzxzajydffflqcuuk) 실측 행을
// MyTier(크리에이터, src/pages/MyTier.jsx)와 AdminCreatorTiers(어드민,
// src/pages/admin/AdminCreatorTiers.jsx) 두 화면이 공통으로 소비하는 화면 모델로 바꾼다.
// 두 화면에서 같은 파생 규칙(4축 매핑, 레이더 정규화, 등급 명칭)을 중복 구현하지 않도록
// 이 파일 하나로 모았다 (2026-08-28, 목업 → 라이브 데이터 배선).
//
// RLS 제약(sources/AGENTS.md 원칙 — 원본/실측치는 추적 가능해야 한다): 일반 authenticated 는
// creator_tier_monthly/daily 에서 본인 이메일 행만 SELECT 할 수 있다
// (creator_tier_monthly_self_select / creator_tier_daily_self_select 정책, qual: user_email =
// auth.jwt()->>'email' OR user_id 매칭 — is_admin() 이면 전체 SELECT). 그래서 "전체 평균"·정규화
// 상수는 크리에이터 화면에서 런타임에 계산할 수 없고, 아래처럼 8월 스냅샷 값을 execute_sql 로
// 실측해 상수로 고정했다. 산정 파이프라인이 다음 달 갱신되면 이 상수들도 재실측해서 교체해야
// 한다(자동 갱신 없음 — 값이 오래되면 "전체 평균" 표시가 부정확해진다).

import { CREATOR_TIER_NAMES, tierIndex } from "./creatorTierNames";
import { tt } from "../i18n/tt";
import { fmtMonth, fmtMonthDay, fmtYearMonthDay, fmtCompact, isKoreanLang } from "./formatLocale";
import {
  totalViewsLabel,
  perVideoViewsLabel,
  performanceAxisInfoText,
  satisfactionAxisInfoText,
  consistencyAxisInfoText,
  performanceBasisLabel as windowPerformanceBasisLabel,
  performanceImprovementText,
} from "./tierWindowCopy";
import { lazyI18n } from "../i18n/lazyText";
import {
  TIER_SATISFACTION_IN_SCORE,
  isBeforeSatisfactionOff,
  tierRowDate,
  isLinearRow,
  isLinearNow,
  fixedCutsForRow,
  LINEAR_FULL,
  LINEAR_WEIGHTS,
} from "./tierSatisfaction";

// ---- band(S~D) ↔ 등급 명칭 매핑 ----
// S(상위 10%)=마스터 / A(~30%)=다이아몬드 / B(~60%)=플래티넘 / C(~85%)=골드 / D(나머지)=실버.
// band 가 null 이면(status='rookie' 행은 band 를 산정하지 않는다) 등급 없음 → null.
// 🔒 값은 **한국어 원문 그대로**(`CREATOR_TIER_NAMES` 와 같은 문자열) — 여기서 나온 이름이 `tierIndex`·
// `TIER_MONTHLY_FEE`·`TIER_COLOR_KEY`·`TierSymbol`·`CREATOR_TIER_CUT_LABELS` 의 **조회 키**로 쓰인다.
// 2026-09-06 다국어 코드모드가 이 표를 `tt()` 로 감싸는 바람에 영어·일본어·중국어에서 "Gold" 가 돌아와
// 멤버십 구독료 예고가 null 이 되고(원장·알약 미표시) 등급 방향·게이지·심볼 조회가 전부 어긋났다(2026-09-07 수정).
// 화면에 보일 때는 각 표시 자리가 `ttl(name)` 로 옮긴다 — `docs/ops/app-i18n-plan.md` §14-2.
const BAND_TO_TIER_NAME = {
  S: "마스터",
  A: "다이아몬드",
  B: "플래티넘",
  C: "골드",
  D: "실버",
};

export function tierNameFromBand(band) {
  return band ? BAND_TO_TIER_NAME[band] ?? null : null;
}

// 현재 등급의 바로 위 등급 명칭. 이미 최상위(마스터)면 null.
export function nextTierName(currentTierName) {
  const idx = tierIndex(currentTierName);
  if (idx < 0 || idx >= CREATOR_TIER_NAMES.length - 1) return null;
  return CREATOR_TIER_NAMES[idx + 1];
}

// ---- 2026-08-29: 하드코딩 실측 상수 전부 삭제 ----
// 신규 테이블 `creator_tier_stats`(월별 1행, RLS: 전체 authenticated SELECT 가능)가 생기면서
// "전체 평균"·정규화 캡을 더 이상 execute_sql 로 실측해 코드에 박아둘 필요가 없어졌다. 화면은
// 이제 `creator_tier_stats`를 본인이 보고 있는 월로 직접 SELECT 해서 쓴다(달마다 값이 자동
// 갱신됨). 주의: 2026-03~06 행의 조회수 관련 값(avg_per_video_views 등)은 0으로 채워져 있어
// 쓰면 안 된다 — 등급 이력 화면 자체가 2026-07 이후만 보여주므로 자연히 이 문제를 피한다.
//
// creator_tier_stats 컬럼: month, avg_satisfaction/performance/consistency/participation/overall,
// avg_per_video_views/p90_per_video_views, avg_total_views/p90_total_views,
// avg_work_video_count/p90_work_video_count, band_cut_s/a/b/c/d, population, formal_population,
// avg_radar_per_video_views/avg_radar_total_views/avg_radar_inspection_participation/
// avg_radar_new_work_participation(2026-08-30 신규 — 아래 레이더 섹션 참고).
//
// 2026-08-30 (사용자 지시, 레이더 6축 재구성에 맞춰 정리): `normalizeToCap()`/`p90_per_video_views`/
// `p90_total_views`/`p90_work_video_count`/`avg_work_video_count` 는 전부 레이더 "전체 평균" 육각형
// 근사에만 쓰이던 것들인데, DB가 `avg_radar_per_video_views`/`avg_radar_total_views` 를 직접 주기
// 시작하면서(레이더와 같은 0~100 스케일 실측 — execute_sql로 재현: 최근 3개월 평균 50~59 범위,
// 백분위 평균이 50 근방인 것과 일치) 더 이상 p90 캡으로 근사할 필요가 없어졌다. 다작량 축 자체도
// 이번에 레이더에서 빠지면서 `avg_work_video_count`/`p90_work_video_count` 를 쓰는 곳이 통째로
// 없어졌다 — 그래서 `normalizeToCap()` 함수를 이 파일(유일한 사용처였음, 다른 페이지에서
// import 하는 곳 없음을 grep 으로 확인)에서 삭제했다. 되살릴 필요가 생기면 이 커밋 이전 버전을
// 참고할 것.

// band(S~D) → creator_tier_stats.band_cut_{s|a|b|c|d} 컬럼 이름. tierName 기준으로 찾는다.
const TIER_NAME_TO_BAND = { 마스터: "S", 다이아몬드: "A", 플래티넘: "B", 골드: "C", 실버: "D" };

// stats(creator_tier_stats 한 행)에서 tierName 등급의 컷오프 점수를 읽는다. stats 없거나 tierName
// 이 유효하지 않으면 null.
export function bandCutForTier(stats, tierName) {
  const band = TIER_NAME_TO_BAND[tierName];
  if (!stats || !band) return null;
  const v = stats[`band_cut_${band.toLowerCase()}`];
  return v === null || v === undefined ? null : Number(v);
}

/**
 * 화면 점수 눈금(displayOverallScore — 만족도 제외 70점 기준)에 맞는 등급 컷.
 * 만족도 제외 기간: 다이아몬드·플래티넘·골드 = 고정 컷(SATISFACTION_OFF_CUTS) · 실버 = 0(바닥) · 마스터 = 새 기준 날짜 이후 통계 행의 관측값.
 * 옛 통계 행(만족도 포함 100점 눈금)은 쓰지 않는다 — 70점 점수와 비교하면 29점쯤 어긋난다(2026-10-26).
 * statsRows: [그날 통계(creator_tier_daily_stats), 그달 통계(creator_tier_stats)] 처럼 우선순위대로. 만족도 포함이면 첫 행 그대로.
 * refRow: 점수를 비교할 기록(오늘 일별 행 등) — 그 기록이 「자료 방식」(2026-11-01 계산~)이면 자료 방식 컷(66.5 / 49 / 28)과
 *         자료 방식 통계만 쓴다. 두 방식은 같은 70점 만점이지만 점수 매기는 법이 달라 컷을 섞으면 안 된다.
 */
export function cutForTierOnScoreScale(tierName, statsRows, refRow = null) {
  if (!tierName) return null;
  const rows = (statsRows || []).filter(Boolean);
  if (TIER_SATISFACTION_IN_SCORE) return rows.length ? bandCutForTier(rows[0], tierName) : null;
  const linear = isLinearRow(refRow);
  const fixed = fixedCutsForRow(refRow);
  if (fixed[tierName] !== undefined) return fixed[tierName];
  if (tierName === "실버") return 0; // 실버는 컷이 없다(골드 컷 미만 전부) — 게이지의 바닥
  // 날짜가 없는 통계 행은 어느 눈금인지 모르므로 쓰지 않는다 · 방식이 다른 통계도 쓰지 않는다
  const src = rows.find((s) => {
    const dt = tierRowDate(s);
    return Boolean(dt) && !isBeforeSatisfactionOff(dt) && isLinearRow(s) === linear;
  });
  return src ? bandCutForTier(src, tierName) : null;
}

// "다음 등급까지 +N.N점" — nextTier 컷오프(band_cut_*)와 본인 overall_score 의 실제 격차.
// 2026-08-29: 순위·백분위 표기는 여전히 금지지만, 본인 점수 대 등급 컷 비교는 순위 노출이
// 아니므로 크리에이터 화면에도 실수치로 보여준다(사용자 확정). 컷/본인 점수가 없거나(다음
// 등급 없음 등) 이미 컷을 넘었으면(재계산 지연 등 방어) null — 호출부는 일반 문구로 폴백한다.
export function nextTierGapText(stats, nextTier, overallScore) {
  const cut = bandCutForTier(stats, nextTier);
  if (cut === null || overallScore === null || overallScore === undefined) return null;
  const gap = cut - Number(overallScore);
  if (gap <= 0) return null;
  return tt("a.3f70b652", "다음 등급까지 +{{v0}}점", { v0: gap.toFixed(1) });
}

// 큰 조회수를 "16.4만" / "3.2억" 스타일로 표시한다. 등급 화면 전용 유틸이며 정산 금액 표기
// (settlementCalculations.js 등)와는 무관하다 — 그쪽 포맷은 손대지 않는다.
export function formatKoreanCount(value) {
  const v = Math.max(0, Number(value) || 0);
  if (!isKoreanLang()) return fmtCompact(v); // 만·억은 한국어 단위 — 다른 언어는 Intl compact(1.5M·16.4K)
  if (v >= 100000000) return tt("a.52266c90", "{{v0}}억", { v0: (v / 100000000).toFixed(1) });
  if (v >= 10000) return tt("a.f99c38ee", "{{v0}}만", { v0: (v / 10000).toFixed(1) });
  return Math.round(v).toLocaleString();
}

function roundScore(v) {
  if (v === null || v === undefined) return null;
  return Math.round(Number(v) * 10) / 10;
}

// ---- 등급을 만드는 네 축(만족도 30 / 성과 40 / 꾸준함 10 / 참여 20 — 2026-08-28 저녁 확정,
// FRONTEND-HANDOFF.md 확정 사항 1번 갱신본. 이전 40/40/10/10 은 폐기됐다) ----
// 2026-10-26 대표 결정(B안) — 만족도는 점수에 넣지 않는다(tierSatisfaction.js). 점수에 들어가는 세 축을 앞에,
// 만족도를 맨 뒤에 두고 「점수 미반영」으로 표시한다. 배점은 70점 만점 기준 점수(40/10/20)로 적는다.
// 2026-10-28 대표 지시 — 점수에 안 들어가는 만족도는 크리에이터·권리사 화면에 아예 보이지 않게 축 목록에서 뺀다(어드민 표는 그대로).
const TIER_AXIS_META = lazyI18n(() =>
  TIER_SATISFACTION_IN_SCORE
    ? [
        { key: "satisfaction", label: tt("a.719de9e6", "만족도"), weightLabel: "30%" },
        { key: "performance", label: tt("a.b3781f65", "성과"), weightLabel: "40%" },
        { key: "consistency", label: tt("a.10ef44c2", "꾸준함"), weightLabel: "10%" },
        { key: "participation", label: tt("a.8c491fa7", "참여"), weightLabel: "20%" },
      ]
    : [
        { key: "performance", label: tt("a.b3781f65", "성과"), weightLabel: tt("tier.satOff.weightPts", "{{v0}}점", { v0: 40 }) },
        { key: "consistency", label: tt("a.10ef44c2", "꾸준함"), weightLabel: tt("tier.satOff.weightPts", "{{v0}}점", { v0: 10 }) },
        { key: "participation", label: tt("a.8c491fa7", "참여"), weightLabel: tt("tier.satOff.weightPts", "{{v0}}점", { v0: 20 }) },
      ]
);
// 「자료 방식」(12월 등급부터 · 2026-11-01 계산~) — 성과 40 · 참여 20 · 꾸준함 10 순서(안내 자료와 같게)
const TIER_AXIS_META_LINEAR = lazyI18n(() => [
  { key: "performance", label: tt("a.b3781f65", "성과"), weightLabel: tt("tier.satOff.weightPts", "{{v0}}점", { v0: LINEAR_WEIGHTS.performance }) },
  { key: "participation", label: tt("a.8c491fa7", "참여"), weightLabel: tt("tier.satOff.weightPts", "{{v0}}점", { v0: LINEAR_WEIGHTS.participation }) },
  { key: "consistency", label: tt("a.10ef44c2", "꾸준함"), weightLabel: tt("tier.satOff.weightPts", "{{v0}}점", { v0: LINEAR_WEIGHTS.consistency }) },
]);
/** 축 목록 — 기록이 있으면 그 기록의 방식, 없으면 오늘 기준 */
function axisMetaFor(row) {
  const linear = row ? isLinearRow(row) : isLinearNow();
  return linear && !TIER_SATISFACTION_IN_SCORE ? TIER_AXIS_META_LINEAR : TIER_AXIS_META;
}

/** 점수에 들어가는 축인가 — 만족도는 2026-10-27 계산부터 점수에 넣지 않는다 */
export function axisCountsToward(key) {
  return TIER_SATISFACTION_IN_SCORE || key !== "satisfaction";
}

// 축 weightLabel 조회 — MyTier.jsx 의 루키 만족도 카드처럼 deriveTierAxes() 전체 대신 특정 축
// 하나의 가중치 라벨만 필요한 곳에서 하드코딩 없이 쓴다(가중치가 또 바뀌어도 TIER_AXIS_META 한
// 곳만 고치면 된다).
export function axisWeightLabel(key) {
  return axisMetaFor(null).find((a) => a.key === key)?.weightLabel ?? "";
}

// 2026-08-31 신규(항목 6, CreatorTierDetail.jsx 전용) — 4축 순서·한글 라벨만 필요하고
// deriveTierAxes()의 basisLabel(검수 건수·조회수 근거 문구)은 필요 없는 호출부를 위한 export.
// get_creator_tier_detail_for_company RPC 는 4축 점수는 주지만 inspection_count/per_video_views/
// total_views 같은 근거 원시값은 안 준다(회사 간 열람 화면이라 의도적으로 최소 표면) — 그 상태로
// deriveTierAxes()를 그대로 쓰면 "검수 0건"/"편당 0회" 같은 오해 소지 라벨이 나온다. 라벨 텍스트
// 자체는 TIER_AXIS_META 하나만 바라보게 여기서 파생한다(별도로 새로 짓지 않음).
export const TIER_AXIS_KEYS = axisMetaFor(null).map((a) => a.key);
export function axisLabel(key) {
  return axisMetaFor(null).find((a) => a.key === key)?.label ?? key;
}

// 2026-08-31 신규(회의 확정, 알파 승인) — "등급을 만드는 네 축" 각 축 라벨 옆 ⓘ 인포 버튼용
// 고정 문구. 회의에서 그대로 준 문구를 코드에 옮긴 것 — 임의로 새로 짓거나 표현을 바꾸지
// 않는다(고정 사전 원칙). 백분위·컷 수치는 여기 포함하지 않는다(이 문구는 축의 "의미"만 설명).
// 2026-09-21 (등급제 v18 — 월 리셋 31일 창 + 유효 조회수, ALGORITHM-SPEC-v17-DRAFT.md §10) —
// satisfaction/performance/consistency 세 문구는 감쇠("최근 ~일수록 더 크게 반영"·"최근 6개월")를
// 설명하고 있었는데, monthly 창 모드에서는 창 안 건수를 가중치 없이 그대로 세므로 더 이상 맞지
// 않다. 문자열 자체는 `src/lib/tierWindowCopy.js`(mode → 문구 순수 함수)로 옮기고 여기서는
// 빌드 상수(TIER_WINDOW_MODE) 기본값으로 호출한다 — decay로 되돌릴 땐 그 파일의 상수 한 줄만
// 바꾸면 이 사전도 같이 돌아간다.
export const TIER_AXIS_INFO_TEXT = lazyI18n(() => ({
  satisfaction: TIER_SATISFACTION_IN_SCORE
    ? satisfactionAxisInfoText()
    : tt("tier.satOff.info", "검수에서 수정 요청 없이 한 번에 통과하는 정도예요. 지금은 기록만 하고 등급 점수에는 넣지 않아요."),
  // 「자료 방식」(12월 등급부터) — 이번 달 목표 대비 달성률
  performance: isLinearNow() && !TIER_SATISFACTION_IN_SCORE
    ? tt("tier.lin2.perfInfo", "이번 달 작품 영상의 유효 조회수 합이에요. {{v0}}회면 만점이고, 모자란 만큼 비율로 점수가 줄어요.", { v0: tt("tier.lin2.fullViews", "1,000만") })
    : performanceAxisInfoText(),
  consistency: isLinearNow() && !TIER_SATISFACTION_IN_SCORE
    ? tt("tier.lin2.consInfo", "이번 달을 7일씩 나눠 칸마다 {{v0}}편을 채웠는지 봐요. 몰아서 올리면 낮고 고르게 올리면 높아요.", { v0: LINEAR_FULL.weekly })
    : consistencyAxisInfoText(),
  participation: isLinearNow() && !TIER_SATISFACTION_IN_SCORE
    ? tt("tier.lin2.partInfo", "이번 달 올린 작품 영상(식별코드) 편수예요. {{v0}}편이면 만점이에요.", { v0: LINEAR_FULL.uploads })
    : tt("a.4bd46040", "검수 참여와 신작 참여를 함께 봐요. 새 작품에 빠르게 참여할수록 좋아요."),
}));

// 4축 key → creator_tier_stats 의 avg_* 컬럼 이름(정확히 일치).
const TIER_AXIS_STATS_FIELD = {
  satisfaction: "avg_satisfaction",
  performance: "avg_performance",
  consistency: "avg_consistency",
  participation: "avg_participation",
};

// TierAxisBar 의 "전체 평균" 세로선 위치 — 2026-08-29부터 creator_tier_stats(해당 월 1행)를
// 직접 읽는다(하드코딩 상수 삭제). stats 가 없으면 50(중간)으로 폴백.
export function tierAxisAveragePercent(stats, axisKey) {
  const field = TIER_AXIS_STATS_FIELD[axisKey];
  if (!stats || !field || stats[field] === null || stats[field] === undefined) return 50;
  return Number(stats[field]);
}

function satisfactionBasisLabel(inspectionCount) {
  if (!TIER_SATISFACTION_IN_SCORE) {
    return inspectionCount > 0
      ? tt("tier.satOff.basis", "검수 {{v0}}건 · 기록만 하고 점수에 넣지 않아요", { v0: inspectionCount })
      : tt("tier.satOff.basisNone", "기록만 하고 점수에 넣지 않아요");
  }
  return inspectionCount > 0 ? tt("a.83e2880a", "검수 {{v0}}건 기준", { v0: inspectionCount }) : tt("a.ed6423c8", "자동 산정 기준");
}

function performanceBasisLabel(perVideoViews, totalViews) {
  return windowPerformanceBasisLabel(formatKoreanCount(perVideoViews), formatKoreanCount(totalViews));
}

// MyTier "등급을 만드는 네 축" 막대에 쓰는 4축 배열. row 는 creator_tier_monthly 한 행(본인 최신월).
// consistency 의 "최근 N주 연속 업로드"(구 목업 basisLabel)는 DB 에 해당 원시값 컬럼이 없어
// 숫자를 지어내지 않고 "최근 활동 리듬 기준"으로 대체했다.
export function deriveTierAxes(row) {
  const linear = !TIER_SATISFACTION_IN_SCORE && isLinearRow(row);
  return axisMetaFor(row).map((meta) => {
    const score = roundScore(row[`${meta.key}_score`]);
    let basisLabel;
    if (meta.key === "satisfaction") basisLabel = satisfactionBasisLabel(row.inspection_count || 0);
    // 「자료 방식」 기록 — total_views 칸이 이번 달 유효 조회수다(서버 tv_eff)
    else if (linear && meta.key === "performance") basisLabel = tt("tier.lin2.perfBasis", "이번 달 유효 조회수 {{v0}}회 · 만점 {{v1}}회", { v0: formatKoreanCount(row.total_views), v1: tt("tier.lin2.fullViews", "1,000만") });
    else if (linear && meta.key === "participation") basisLabel = tt("tier.lin2.partBasis", "이번 달 업로드 · 만점 {{v0}}편", { v0: LINEAR_FULL.uploads });
    else if (linear && meta.key === "consistency") basisLabel = tt("tier.lin2.consBasis", "7일마다 {{v0}}편이 만점", { v0: LINEAR_FULL.weekly });
    else if (meta.key === "performance") basisLabel = performanceBasisLabel(row.per_video_views, row.total_views);
    else if (meta.key === "consistency") basisLabel = tt("a.28877a12", "최근 활동 리듬 기준");
    else basisLabel = tt("a.541e169d", "검수 참여 · 신작 참여 평균");
    return {
      key: meta.key,
      label: meta.label,
      weightLabel: meta.weightLabel,
      basisLabel,
      score: score ?? 0,
      barPercent: score ?? 0,
    };
  });
}

// 만족도 근거 뱃지: 검수 건수가 있으면 실측, 0건이면 자동 산정만(어드민 표 컬럼 + MyTier 루키
// 만족도 카드가 공유).
export function satisfactionEvidenceLabel(inspectionCount) {
  return (inspectionCount || 0) > 0 ? tt("a.2f91fb77", "실측") : tt("a.bcc3b820", "자동만");
}

// ---- 활동 성향 레이더(6축 — MyTier.jsx ActivityRadarChart) ----
// 2026-08-30 (사용자 지시, DB 쪽 radar 컬럼 재구성 반영): 다작량(radar_volume)·활동 이력
// (radar_history) 축이 빠지고 **검수 참여**(radar_inspection_participation)·**신작 참여**
// (radar_new_work_participation) 축으로 대체됐다 — execute_sql 로 creator_tier_monthly/daily
// 스키마에서 radar_volume/radar_history 컬럼 자체가 사라지고 두 신규 컬럼이 생긴 것을 확인 후
// 착수. 축 순서(검수 통과/편당 조회수/총 조회수/활동 리듬/검수 참여/신작 참여)는 이 배열이 유일한
// 출처 — MyTier(크리에이터)만 이 배열을 실제로 그리고, AdminCreatorTiers.jsx 의 `MiniRadar` 는
// (grep 으로 재확인) `deriveRadarValues`/`RADAR_AXIS_META` 를 쓰지 않는 별개의 단순화된 4값
// 순환 배치라 이번 변경과 무관하다.
// 2026-09-21 (등급제 v18) — viewsPerVideo/totalViews 두 축 라벨은 조회수 표현이라
// tierWindowCopy.js 를 거친다(monthly 모드: "편당 유효 조회수"/"최근 31일 유효 조회수").
// 2026-10-28 대표 지시 — 검수 승인 칸은 만족도와 같은 값이라, 만족도를 점수에 넣지 않는 동안은 레이더에서 뺀다(5축).
const RADAR_AXIS_META = lazyI18n(() => [
  ...(TIER_SATISFACTION_IN_SCORE ? [{ key: "inspectionPass", label: tt("a.e852973d", "검수 승인") }] : []),
  { key: "viewsPerVideo", label: perVideoViewsLabel() },
  { key: "totalViews", label: totalViewsLabel() },
  { key: "activityRhythm", label: tt("a.321b6582", "활동 리듬") },
  { key: "inspectionParticipation", label: tt("a.ca10acfb", "검수 참여") },
  { key: "newWorkParticipation", label: tt("a.bd504adf", "신작 참여") },
]);

function scoreToUnit(v) {
  return Math.max(0, Math.min(1, (Number(v) || 0) / 100));
}

// row 의 radar_* 6컬럼(0~100, DB 산정 파이프라인이 직접 채운다)이 전부 있는지 — 없는 행(1건
// 존재 가능, 2026-08-29 확인 — 2026-08-30 컬럼 재구성 후에도 동일 건수로 재확인)은 "지표 준비
// 중"으로 안전 처리해야 한다.
export function radarIsReady(row) {
  return RADAR_ROW_FIELDS.every((f) => row?.[f] !== null && row?.[f] !== undefined);
}

const RADAR_ROW_FIELDS = [
  "radar_pass_rate",
  "radar_per_video_views",
  "radar_total_views",
  "radar_rhythm",
  "radar_inspection_participation",
  "radar_new_work_participation",
];

// row: creator_tier_monthly 한 행. stats: 같은 월의 creator_tier_stats 한 행(없으면 average 는
// 전부 0).
// "나" 값은 DB가 이미 계산해 저장한 radar_* 6컬럼(0~100)을 그대로 쓴다(ALGORITHM-SPEC-v15.md
// 6절 산식과 프론트 근사식이 어긋날 여지를 없앤다).
// "전체 평균" 육각형: 2026-08-30부터 편당 조회수·총 조회수·검수 참여·신작 참여 네 축은
// `creator_tier_stats.avg_radar_*` 를 그대로 쓴다(신규 컬럼, radar_* 와 같은 0~100 스케일 —
// execute_sql 로 실측: 최근 3개월 평균이 50~59 범위로 백분위 평균 특성과 일치) — 예전처럼
// p90 캡으로 근사할 필요가 없어졌다. 검수 통과·활동 리듬 두 축은 여전히 전용 평균 컬럼이 없어
// avg_satisfaction/avg_consistency 로 근사하는 기존 방식을 유지한다(그 두 축은 radar_* 산식이
// 곧 해당 점수와 같다는 전제).
export function deriveRadarValues(row, stats) {
  const me = radarIsReady(row)
    ? [
        scoreToUnit(row.radar_pass_rate),
        scoreToUnit(row.radar_per_video_views),
        scoreToUnit(row.radar_total_views),
        scoreToUnit(row.radar_rhythm),
        scoreToUnit(row.radar_inspection_participation),
        scoreToUnit(row.radar_new_work_participation),
      ]
    : new Array(6).fill(0);
  const average = stats
    ? [
        scoreToUnit(stats.avg_satisfaction),
        scoreToUnit(stats.avg_radar_per_video_views),
        scoreToUnit(stats.avg_radar_total_views),
        scoreToUnit(stats.avg_consistency),
        scoreToUnit(stats.avg_radar_inspection_participation),
        scoreToUnit(stats.avg_radar_new_work_participation),
      ]
    : new Array(6).fill(0);
  // 만족도를 점수에 넣지 않는 동안 검수 승인(= 만족도) 칸을 뺀다 — axes 와 같은 길이
  const keep = TIER_SATISFACTION_IN_SCORE ? (arr) => arr : (arr) => arr.slice(1);
  return { axes: RADAR_AXIS_META, me: keep(me), average: keep(average), ready: radarIsReady(row) };
}

// 레이더 콜아웃 문구 — performance/consistency/participation 중 가장 높은 축을 기준으로 고정
// 문구 사전에서 그대로 고른다(만족도는 대부분 90%대라 변별력이 없어 제외). 개인화 서술을
// 새로 짓지 않는다.
const RADAR_CALLOUT_TEXT = lazyI18n(() => ({
  performance: tt("a.58fd5fa3", "성과 · 조회수형 크리에이터"),
  consistency: tt("a.2651bd68", "꾸준함형 크리에이터"),
  participation: tt("a.31f4c73a", "참여형 크리에이터"),
}));

export function deriveRadarCallout(row) {
  const candidates = [
    ["performance", row.performance_score],
    ["consistency", row.consistency_score],
    ["participation", row.participation_score],
  ];
  const top = candidates.reduce((best, cur) => ((cur[1] ?? -1) > (best[1] ?? -1) ? cur : best));
  return RADAR_CALLOUT_TEXT[top[0]] || "";
}

// ---- 히어로 요약 / "OO까지 남은 것" — 축별 고정 문구 사전(개인화 문구 조작 금지) ----
const AXIS_STRENGTH_TEXT = lazyI18n(() => ({
  satisfaction: tt("a.e29c2925", "만족도 축이 강점이에요"),
  performance: tt("a.6fc24149", "성과 축이 강점이에요"),
  consistency: tt("a.0db214d3", "꾸준함 축이 강점이에요"),
  participation: tt("a.066a53fa", "참여 축이 강점이에요"),
}));

const AXIS_IMPROVEMENT_TEXT = lazyI18n(() => isLinearNow() && !TIER_SATISFACTION_IN_SCORE ? ({
  satisfaction: tt("a.8579c328", "검수 결과가 쌓이면 만족도가 더 정확히 반영돼요"),
  performance: tt("tier.lin2.perfImprove", "이번 달 조회수가 {{v0}}회에 가까울수록 성과 점수가 올라가요", { v0: tt("tier.lin2.fullViews", "1,000만") }),
  consistency: tt("tier.lin2.consImprove", "7일마다 {{v0}}편씩 고르게 올리면 꾸준함 점수가 올라가요", { v0: LINEAR_FULL.weekly }),
  participation: tt("tier.lin2.partImprove", "이번 달 작품 영상을 {{v0}}편에 가깝게 올릴수록 참여 점수가 올라가요", { v0: LINEAR_FULL.uploads }),
}) : ({
  satisfaction: tt("a.8579c328", "검수 결과가 쌓이면 만족도가 더 정확히 반영돼요"),
  performance: performanceImprovementText(),
  consistency: tt("a.e6cc25e2", "매주 업로드를 유지하면 다음 등급 결정에 도움이 돼요"),
  participation: tt("a.f338c205", "검수·신작 참여를 늘리면 참여 축이 올라가요"),
}));

// tierAxes: deriveTierAxes() 결과(4개). 점수가 가장 높은/낮은 축을 하나씩 골라 고정 문구
// 사전에서 그대로 조합한다 — 개인화된 서술을 새로 생성하지 않는다.
// 점수에 안 들어가는 축(만족도 — 2026-10-26)은 강점·보완 후보에서 뺀다.
export function deriveHeroSummary(tierAxes) {
  const scored = tierAxes.filter((a) => a.score !== null && a.score !== undefined && axisCountsToward(a.key));
  if (scored.length === 0) return "";
  const top = scored.reduce((best, cur) => (cur.score > best.score ? cur : best));
  const bottom = scored.reduce((worst, cur) => (cur.score < worst.score ? cur : worst));
  if (top.key === bottom.key) return AXIS_STRENGTH_TEXT[top.key];
  return `${AXIS_STRENGTH_TEXT[top.key]}. ${AXIS_IMPROVEMENT_TEXT[bottom.key]}`;
}

// "OO까지 남은 것" 카드 — 점수 낮은 순 최대 3개, 고정 문구 사전만 사용.
export function deriveToNextTierCards(tierAxes) {
  const scored = tierAxes.filter((a) => a.score !== null && a.score !== undefined && axisCountsToward(a.key));
  return [...scored]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map((a) => ({ axisLabel: a.label, text: AXIS_IMPROVEMENT_TEXT[a.key] }));
}

// ---- 어드민 순위표 행 매핑 ----
// row: creator_tier_monthly 한 행(최신 월 전체 조회). rank 는 호출부가 overall_score 내림차순으로
// 클라이언트에서 부여해서 넘긴다(루키는 rank=null).
// name/email: name 은 표시용(기본값 user_email 그대로) — AdminCreatorTiers.jsx 가 이 함수 호출
// 뒤에 user."name"/대표 채널 정보로 실명을 덮어쓴다(2026-08-29, 아래 email 필드를 조인 키로
// 쓴다). email 은 선택/그룹핑에 쓰는 진짜 고유 식별자이므로 절대 덮어쓰지 않는다 — 실명이
// 동명이인이면 name 만으로는 행을 구분할 수 없기 때문. 이 화면은 어드민 전용이라 가명 처리
// 대상이 아니다(가명은 mocks/adminCreatorTiersMock.js 목업 한정 — CSV 실명이 코드에 남지 않게
// 하려는 조치였다).
export function mapMonthlyRowToAdminRow(row, rank) {
  const tier = tierNameFromBand(row.band);
  const statusBadges = [];
  if (row.status === "rookie") statusBadges.push("루키");
  // 2026-08-28 밤: 휴면 판정은 신규 is_dormant 컬럼을 쓴다(기존 status==='dormant' 텍스트가 아님).
  // status 는 "rookie 우선" 규칙이라 루키이면서 동시에 휴면인 사람도 status='rookie'로만 남는데,
  // is_dormant 는 그 둘을 별도로 알려준다 — 루키 출신 휴면도 여기서 "휴면" 배지가 함께 붙는다.
  if (row.is_dormant) statusBadges.push("휴면");
  return {
    key: row.user_email,
    email: row.user_email,
    rank: rank ?? null,
    name: row.user_email,
    userId: row.user_id,
    tier,
    satisfaction: roundScore(row.satisfaction_score),
    performance: roundScore(row.performance_score),
    consistency: roundScore(row.consistency_score),
    participation: roundScore(row.participation_score),
    inspectionCount: row.inspection_count || 0,
    satisfactionEvidence: satisfactionEvidenceLabel(row.inspection_count),
    status: statusBadges,
    rookieProgress:
      row.status === "rookie"
        ? { current: row.rookie_progress_current ?? 0, target: row.rookie_progress_target ?? 5 }
        : undefined,
  };
}

// "YYYY-MM-DD" (date 컬럼) → "8월" 같은 짧은 라벨.
export function monthLabel(monthStr) {
  if (!monthStr) return "";
  const m = parseInt(monthStr.slice(5, 7), 10);
  return fmtMonth(m);
}

// 2026-08-31 네 번째(D-day 버그 수정, 사용자 실측 지적) — 아래 두 함수(`nextConfirmDateLabel`/
// `daysUntilNextConfirm`)가 "말일" 자체를 목표일로 계산해 8/31에 "D-30"(목표=9/30)을 보여주는
// 버그가 있었다. 실제 DB 크론은 **매월 1일 00:05 KST에 전월을 확정**한다(이 파일 상단 커밋
// 이력의 "말일 평가·1일 적용" 문구는 그 시각을 사용자에게 설명하는 근사 표현일 뿐, 실제 확정
// "사건"은 언제나 어떤 달의 "1일"에 일어난다) — 그래서 "다음 확정"은 오늘이 며칠이든 **항상
// 다음 달 1일**이어야 한다(오늘이 1일이어도 그날 00:05에 이미 실행됐다고 보고 그다음 달 1일까지
// 센다 — "1일 확정 직후엔 D-30~31" 요구사항과 일치). 예전의 "이번 달 말일 vs 다음 달 말일"
// 분기(그리고 그걸 위해 있던 `lastDayOfMonth` 헬퍼)는 필요 없어져 함께 제거했다 — 목표일 계산이
// 더 이상 그 달의 마지막 날짜가 몇 일인지 알 필요가 없기 때문(항상 "1일"). referenceDate는
// 테스트/디버깅용 주입 지점. 8/31·9/1·9/15 세 시점 시뮬레이션으로 검증(Node, 보고에 명시).
// ---- 2026-09-01 대표 확정 — 등급 시점 어휘(화면 전체 공통) ----------------------
// "확정"이 무엇을 확정한다는 말인지 한 번에 읽히지 않는다는 지적에서 나왔다. 후보 다섯(결정 /
// 새 등급 / 재평가 / 시즌 / 산정)을 놓고 **결정**으로 정했다. 세 가지 말만 쓴다:
//
//   사건(매월 1일 00:05 KST)          → "등급 결정"    예) "등급 결정까지 29일", "10월 1일 결정"
//   그렇게 정해져 지금 적용 중인 등급  → "{월} 등급"    예) "9월 등급"  (좁은 트랙에선 "적용 중")
//   오늘 데이터로 미리 본 등급         → "오늘 기준"    (기존 유지)
//
// "10월 1일에 결정되어 적용됩니다"로 한 문장에 이어지는 것이 이 세트를 고른 이유다.
// **화면 문구에 "확정"·"갱신"을 다시 쓰지 말 것** — 갱신은 "계약 연장"으로도 읽혀서 *다시
// 정해진다*는 뜻이 흐려진다. "새 등급까지"도 쓰지 않는다(게이지의 "마스터까지 +N점"과 겹쳐
// 둘 다 승급 이야기로 읽힌다). 함수·변수명의 `confirm` 계열은 DB 컬럼·크론과 맞춘 것이라
// 그대로 둔다 — 바꾸는 건 화면에 나가는 문자열뿐이다.
// 날짜는 반드시 아래 `nextConfirmDateLabel()` 에서 가져온다(문자열을 직접 만들면 크론과 어긋남).
export function nextConfirmDateLabel(referenceDate = new Date()) {
  const y = referenceDate.getFullYear();
  const m = referenceDate.getMonth(); // 0-indexed
  const targetMonthIndex = m + 1; // 항상 다음 달 1일 — Date 생성자가 12를 넘으면 알아서 다음 해로 넘긴다
  const targetYear = y + Math.floor(targetMonthIndex / 12);
  const targetMonthIndex0 = targetMonthIndex % 12;
  const targetMonth = targetMonthIndex0 + 1; // 1-indexed
  return targetYear === y ? fmtMonthDay(targetMonth, 1) : fmtYearMonthDay(targetYear, targetMonth, 1);
}

// "YYYY-MM-DD" (creator_tier_daily.computed_date) → "8/29" 같은 짧은 날짜 라벨.
// 2026-08-29: MyTier 히어로의 "현재 추이"가 항상 기준일을 명시하도록 신설(사용자 피드백 —
// 확정과 추이가 같으면 추이 표시가 사실상 안 보이던 문제 대응).
export function shortDateLabel(dateStr) {
  if (!dateStr) return "";
  const m = parseInt(dateStr.slice(5, 7), 10);
  const d = parseInt(dateStr.slice(8, 10), 10);
  return `${m}/${d}`;
}

// ---- 2026-08-30 신규 — TopNavBar 드롭다운 등급 카드 + Settings(내 정보) 등급 트랙 전용 파생.
// 알파 UI 확정 패키지(PR #141)의 `tierDisplay.js`에서 반입/각색하되, 등급 이름·순서를 새 영문
// 배열(TRACK_TIER_KEYS)로 병행 도입하지 않고 기존 `CREATOR_TIER_NAMES`/`tierIndex()`(바로 위
// import, 이미 실버→마스터 순)를 그대로 재사용한다 — "같은 값이 화면마다 다른 소스에서 나오는"
// 상황을 피하기 위함(코디네이터 지시).

// 등급 트랙(TierTrack) 위 두 마커의 상태 계산 — 확정(월등급) 칸과 오늘(일등급) 마커의 위치·방향.
// confirmedTier/dailyTier는 한글 등급명(CREATOR_TIER_NAMES 값). 트랙에 없는 값(예: 루키)은
// index -1로 떨어진다.
// isHolding = 오늘 위치가 확정 칸보다 왼쪽(하락). 이때 마커를 웜그레이로 바꾸고 "유지 구간"
// 문구를 쓴다 — 경고색(빨강)은 쓰지 않는다(스펙 5절, 인격이 아니라 상태를 말한다는 규칙).
export function resolveTierTrackState({ confirmedTier, dailyTier, dormant = false } = {}) {
  const confirmedIndex = tierIndex(confirmedTier);
  const todayIndex = tierIndex(dailyTier);
  const hasToday = todayIndex >= 0 && !dormant;

  let direction = "same";
  if (hasToday && confirmedIndex >= 0) {
    if (todayIndex > confirmedIndex) direction = "up";
    else if (todayIndex < confirmedIndex) direction = "down";
  }

  return {
    confirmedIndex,
    todayIndex,
    direction,
    isHolding: direction === "down",
    hasToday,
    dormant: !!dormant,
  };
}

// 트랙 가로 위치 비율(0~1). 기본은 해당 등급 칸의 한가운데 — ratioWithinTier(0~1)로 칸 안에서의
// 정확한 위치를 지정할 수 있다(현재 호출부는 전부 기본값 0.5만 씀 — 칸 안 정밀 위치는 미배선).
export function tierTrackRatio(tierName, ratioWithinTier = 0.5) {
  const idx = tierIndex(tierName);
  if (idx < 0) return 0;
  const clamped = Math.min(1, Math.max(0, ratioWithinTier));
  return (idx + clamped) / CREATOR_TIER_NAMES.length;
}

// 다음 월 확정(매월 1일 00:05 KST, 전월 확정)까지 남은 일수 — `nextConfirmDateLabel()`과 같은
// "항상 다음 달 1일" 규칙(위 함수 주석 참고, 그쪽이 바뀌면 이 함수도 같이 고칠 것). referenceDate
// 는 테스트/디버깅용 주입 지점. 날짜만 비교(시각 절삭)하므로 오늘이 1일이면 그다음 달 1일까지
// 정확히 그 달의 일수(28~31)를 반환한다(0을 반환하는 경우가 없다 — 확정일 당일이 곧 "다음"이
// 되는 경계가 사라졌기 때문, 8/31·9/1·9/15 세 시점 Node 시뮬레이션으로 검증).
export function daysUntilNextConfirm(referenceDate = new Date()) {
  const y = referenceDate.getFullYear();
  const m = referenceDate.getMonth(); // 0-indexed
  const day = referenceDate.getDate();
  const targetMonthIndex = m + 1; // 항상 다음 달 1일
  const targetYear = y + Math.floor(targetMonthIndex / 12);
  const targetMonthIndex0 = targetMonthIndex % 12;
  const target = new Date(targetYear, targetMonthIndex0, 1);
  const today = new Date(y, m, day);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

// "D-26" / 당일이면 "D-DAY" — 드롭다운 등급 카드 우측 표기(스펙 1절).
export function formatDDay(days) {
  if (typeof days !== "number" || !Number.isFinite(days)) return "";
  if (days <= 0) return "D-DAY";
  return `D-${Math.floor(days)}`;
}

// 2026-08-30(사용자 스크린샷 확인, 텍스트 왜곡 수정 — 공용 헬퍼) — `preserveAspectRatio="none"`로
// 가로/세로를 독립적으로 늘린 SVG 안에 `<text>`를 두면, SVG 자체의 비균등 좌표 변환이 글리프에도
// 그대로 적용돼 글자가 기울어지거나 눌려 보인다(회전/왜곡). 그래서 `TierHistoryChart`(MyTier.jsx
// 로컬)·`DailyTrendSparkline`(2026-08-31 세 번째, `src/components/DailyTrendSparkline.jsx`로
// 추출됨) 양쪽 다 텍스트는 전부 SVG 밖 HTML 오버레이로 빼고, 이 함수로 SVG textAnchor와 같은
// 의미의 앵커(start/middle/end)를 CSS transform으로 표현한다 — left/top(px 또는 %)을 텍스트가
// "가리키는 지점"으로 두고 transform으로 그 지점 기준 정렬을 맞춘다. 세로는 항상 가운데 정렬로
// 통일. 2026-08-31 세 번째(항목 6-②) — 두 화면(MyTier.jsx/CreatorTierDetail.jsx)이 같은
// `DailyTrendSparkline` 컴포넌트를 공유하게 되면서, 그 컴포넌트가 의존하던 이 헬퍼도 페이지
// 로컬에서 이 공용 lib으로 옮겼다(중복 정의 대신 단일 출처).
export function chartLabelAnchorTransform(anchor) {
  if (anchor === "start") return "translate(0, -50%)";
  if (anchor === "end") return "translate(-100%, -50%)";
  return "translate(-50%, -50%)";
}
